import { Checkbox, Form, Switch } from 'tio-ui/components/forms';
import type { FormResultData } from 'tio-ui/components/forms';
import { contains, not, or } from 'tio-ui/utilities';
import { PhCheck, PhX } from 'ember-phosphor-icons';
import { on } from '@ember/modifier';
import { array, fn } from '@ember/helper';
import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import RelativeDateRangePicker from '../../../forms/relative-date-range-picker.gts';
import type { ApplicationFilterSignature as S } from '../types.gts';
import type EnumModel from '../../../../models/enum';
import type IntlService from 'ember-intl/services/intl';
import type StoreService from '@ember-data/store';
import type FeaturesService from '../../../../services/features.ts';
import { BASE_QUERY } from '../queries.gts';

// Form data structure for nested enum data
// When checkbox names contain dots (e.g., "TAS.ApplicationState"), Frontile creates nested objects
// Result structure: { data: { TAS: { ApplicationState: { pending: true, approved: false } } }, isValid: boolean, errors: {...}, dirty: boolean }
interface ApplicationStatesFormData {
  TAS?: {
    ApplicationState?: Record<string, boolean>;
  };
}

interface ApplicationFilterSignature {
  Args: S & {
    onFilterUpdate: (filterUpdates: Record<string, unknown>) => void;
  };
  Blocks: {
    default: [];
  };
}

export default class ApplictionFilter extends Component<ApplicationFilterSignature> {
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @service declare features: FeaturesService;

  // Remove the tracked property and make it a computed getter instead
  get selectedApplicationStates(): string[] {
    return this.args.applicationStates
      ? this.args.applicationStates.split(',').filter(Boolean)
      : [];
  }

  get stateOptions() {
    return (
      this.applicationStateOptionsData.value
        ?.filter((state) => state.metadata['resting-state'])
        ?.map((state) => ({
          label: this.intl.t(`common.status_message.to.${state.dot}`),
          key: state.dot,
        })) || []
    );
  }

  applicationStateOptionsData = trackedFunction(this, async () => {
    const results = (await this.store.query('enum', {
      filter: {
        flavor: 'TAS.ApplicationState',
      },
    })) as EnumModel[];

    return results;
  });

  individualApplicationsDidChange = (value: boolean, _event: Event) => {
    // Clear application states by setting to empty string
    this.args.onFilterUpdate({
      ...BASE_QUERY,
      applicationStates: '', // Clear the application states
      showIndividualApplications: value,
    });
  };

  // Empty submit handler - Form component requires @onSubmit even if only using @onChange
  selectedApplicationStatesDidSubmit = (
    _result: FormResultData<ApplicationStatesFormData>,
    _event: SubmitEvent
  ) => {
    // No-op for form submission
  };

  selectedApplicationStatesDidChange = (result: FormResultData<ApplicationStatesFormData>) => {
    const selectedStates: string[] = [];

    const statesByFlavor = result.data.TAS?.ApplicationState || {};

    for (const [key, value] of Object.entries(statesByFlavor)) {
      // Include only keys where the checkbox is checked (true)
      if (value === true) {
        selectedStates.push(`TAS.ApplicationState.${key}`);
      }
    }

    const applicationStates = selectedStates.join(',');
    this.args.onFilterUpdate({ applicationStates: applicationStates });
  };

  get isRelativeDateFiltersEnabled(): boolean {
    return this.features.isEnabled('relativeDateFilters');
  }

  applicationDateRangeDidChange = (update: {
    startDate: string;
    endDate: string;
    presetId: string;
  }) => {
    this.args.onFilterUpdate({
      applicationCreatedAtStart: update.startDate,
      applicationCreatedAtEnd: update.endDate,
      applicationDateRangePreset: update.presetId,
    });
  };

  // Fallback handlers for standard date pickers (when flag is disabled)
  applicationCreatedAtStartDidChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : '';
    this.args.onFilterUpdate({ applicationCreatedAtStart: dateString });
  };

  applicationCreatedAtEndDidChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : '';
    this.args.onFilterUpdate({ applicationCreatedAtEnd: dateString });
  };

  <template>
    <div class="flex items-center justify-between">
      <span class="text-sm font-medium text-gray-900 dark:text-gray-100">Show Individual
        Applications</span>
      <Switch
        @isSelected={{or @showIndividualApplications false}}
        @onChange={{this.individualApplicationsDidChange}}
        @intent="primary"
      >
        <:thumbContent as |ctx|>
          {{#if ctx.isSelected}}
            <PhCheck @size="12" />
          {{else}}
            <PhX @size="12" />
          {{/if}}
        </:thumbContent>
      </Switch>
    </div>
    <div
      class="{{if
          @showIndividualApplications
          'max-h-dvh overflow-visible'
          'max-h-0 overflow-hidden'
        }}
        flex flex-col gap-y-3 transition-all duration-500"
    >
      <div class="ml-4 flex flex-col gap-y-3">
        {{#if this.applicationStateOptionsData.isLoading}}
          <div class="text-sm text-gray-500 dark:text-gray-400">Loading application states...</div>
        {{else if this.applicationStateOptionsData.isFinished}}
          {{#if this.stateOptions.length}}
            <Form
              @onChange={{this.selectedApplicationStatesDidChange}}
              @onSubmit={{this.selectedApplicationStatesDidSubmit}}
              class="flex flex-col gap-y-3"
            >
              {{#each this.stateOptions as |data|}}
                <Checkbox
                  name={{data.key}}
                  @label={{data.label}}
                  @checked={{contains this.selectedApplicationStates data.key}}
                  @size="sm"
                />
              {{/each}}
            </Form>
          {{else}}
            <div class="text-sm text-gray-500 dark:text-gray-400">No application states available</div>
          {{/if}}
        {{else}}
          <div class="text-sm text-gray-500 dark:text-gray-400">Initializing...</div>
        {{/if}}

        {{#if this.isRelativeDateFiltersEnabled}}
          <RelativeDateRangePicker
            @label="Application Creation Date"
            @startDate={{@applicationCreatedAtStart}}
            @endDate={{@applicationCreatedAtEnd}}
            @selectedPreset={{@applicationDateRangePreset}}
            @onDateRangeChange={{this.applicationDateRangeDidChange}}
            @disabled={{not @showIndividualApplications}}
          />
        {{else}}
          {{! Fallback to standard date pickers when feature flag is disabled }}
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-gray-900 dark:text-gray-100">Application Creation
              Date</label>
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-700 dark:text-gray-300">From</span>
              <div class="flex-1 relative">
                {{! @glint-expect-error: return to this, follow up with dev}}
                <EmberFlatpickr
                  @date={{or @applicationCreatedAtStart ""}}
                  @onChange={{this.applicationCreatedAtStartDidChange}}
                  @dateFormat="Y-m-d"
                  @altInput={{true}}
                  @altFormat="F j, Y"
                  placeholder="mm/dd/yyyy"
                  disabled={{not @showIndividualApplications}}
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100"
                />
                {{#if @applicationCreatedAtStart}}
                  <button
                    type="button"
                    {{on "click" (fn this.applicationCreatedAtStartDidChange (array))}}
                    disabled={{not @showIndividualApplications}}
                    class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Clear start date"
                  >
                    <PhX @size="16" />
                  </button>
                {{/if}}
              </div>
              <span class="text-sm text-gray-700 dark:text-gray-300">To</span>
              <div class="flex-1 relative">
                {{! @glint-expect-error: return to this, follow up with dev}}
                <EmberFlatpickr
                  @date={{or @applicationCreatedAtEnd ""}}
                  @onChange={{this.applicationCreatedAtEndDidChange}}
                  @dateFormat="Y-m-d"
                  @altInput={{true}}
                  @altFormat="F j, Y"
                  @minDate={{or @applicationCreatedAtStart ""}}
                  placeholder="mm/dd/yyyy"
                  disabled={{not @showIndividualApplications}}
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100"
                />
                {{#if @applicationCreatedAtEnd}}
                  <button
                    type="button"
                    {{on "click" (fn this.applicationCreatedAtEndDidChange (array))}}
                    disabled={{not @showIndividualApplications}}
                    class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Clear end date"
                  >
                    <PhX @size="16" />
                  </button>
                {{/if}}
              </div>
            </div>
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
